import Dagre from '@dagrejs/dagre';
import { type Edge, MarkerType, type Node } from '@xyflow/react';
import i18n from '@/translations';
import type {
  CompletionNodeData,
  ExternalRedirectNodeData,
  FunnelData,
  OrderBumpNodeData,
  ProductFunnelNodeData,
  ThankYouNodeData,
  UpsellNodeData,
} from './funnelTypes';

const NODE_W: Record<string, number> = {
  productFunnelNode: 400,
  orderBumpNode: 260,
  upsellNode: 280,
  completionNode: 210,
  externalRedirectNode: 230,
  thankYouNode: 250,
};
const NODE_H: Record<string, number> = {
  productFunnelNode: 280,
  orderBumpNode: 155,
  upsellNode: 370,
  completionNode: 80,
  externalRedirectNode: 90,
  thankYouNode: 95,
};

const EC = {
  default: '#A9D342',
  orderBump: '#F59E0B',
  accept: '#22C55E',
  decline: '#EF4444',
  purchase: '#A9D342',
  thankYou: '#8B5CF6',
};

function mkEdge(
  id: string,
  src: string,
  tgt: string,
  opts: {
    color?: string;
    label?: string;
    animated?: boolean;
    srcH?: string;
    tgtH?: string;
    dash?: boolean;
    desc?: string;
    metrics?: string;
  } = {},
): Edge {
  const {
    color = EC.default,
    label,
    animated = true,
    srcH,
    tgtH,
    dash = false,
    desc,
    metrics,
  } = opts;
  return {
    id,
    source: src,
    target: tgt,
    type: 'animatedEdge',
    sourceHandle: srcH,
    targetHandle: tgtH,
    label,
    animated,
    style: {
      stroke: color,
      strokeWidth: 2,
      strokeDasharray: dash ? '6 3' : undefined,
    },
    markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
    data: { color, description: desc, metrics },
  };
}

export function buildFunnelGraph(
  data: FunnelData | null,
): { nodes: Node[]; edges: Edge[] } {
  if (!data) return { nodes: [], edges: [] };

  const mainNodes: Node[] = [];
  const mainEdges: Edge[] = [];
  const auxNodes: { node: Node; parentId: string; side: 'left' | 'right'; idx: number; total: number }[] = [];
  const auxEdges: Edge[] = [];

  const upsellMap = new Map(data.upsells.map((u) => [u.id, u]));
  let cmpCtr = 0;

  const prodPerf = data.performance?.product;

  const offerInfos = data.offers.map((offer) => {
    const offerPerf = data.performance?.offers?.[offer.id];
    const checkoutInfos = (offer.checkouts || []).map((ck) => ({
      id: ck.id,
      name: ck.name,
      link: ck.link,
      isActive: ck.is_active,
    }));
    return {
      id: offer.id,
      name: offer.name,
      priceFormatted: offer.price_formatted,
      currency: offer.currency,
      paymentType: offer.payment_type,
      recurringInterval: offer.recurring_interval ?? undefined,
      conversions: offerPerf?.conversions,
      revenue: offerPerf?.revenue,
      checkouts: checkoutInfos,
    };
  });

  const prodId = `product-${data.product.id}`;
  mainNodes.push({
    id: prodId,
    type: 'productFunnelNode',
    data: {
      type: 'productFunnel',
      label: data.product.name,
      productId: data.product.id,
      status: data.product.status,
      imageUrl: data.product.image_url ?? undefined,
      totalSales: prodPerf?.total_sales,
      revenue: prodPerf?.revenue,
      offers: offerInfos,
    } satisfies ProductFunnelNodeData,
    position: { x: 0, y: 0 },
  });

  const bumpIds = data.product_to_bumps || [];
  bumpIds.forEach((bid, idx) => {
    const bump = data.order_bumps.find((b) => b.id === bid);
    if (!bump) return;
    const bnid = `bump-${bump.id}`;
    auxNodes.push({
      node: {
        id: bnid,
        type: 'orderBumpNode',
        data: {
          type: 'orderBump',
          label: bump.display_text,
          offerName: bump.offer_name ?? '',
          priceFormatted: bump.offer_price_formatted ?? '',
          callToAction: bump.call_to_action,
          orderBumpId: bump.id,
          isSecret: bump.is_secret ?? false,
          imageUrl: bump.image_url ?? undefined,
        } satisfies OrderBumpNodeData,
        position: { x: 0, y: 0 },
      },
      parentId: prodId,
      side: 'right',
      idx,
      total: bumpIds.length,
    });
    auxEdges.push(
      mkEdge(`e-prod-bump-${bump.id}`, prodId, bnid, {
        color: EC.orderBump,
        dash: true,
        srcH: 'bump',
        desc: i18n.t('funnel:edge_shown_in_checkout'),
      }),
    );
  });

  let extCtr = 0;

  function addTerminalNode(
    upsell: typeof data.upsells[0],
    unid: string,
    direction: 'accept' | 'decline',
  ) {
    const edgeColor = direction === 'accept' ? EC.accept : EC.decline;
    const srcH = direction;
    const externalPage = direction === 'accept' ? upsell.accepted_page : upsell.recused_page;
    const suffix = i18n.t(direction === 'accept' ? 'funnel:suffix_accepted' : 'funnel:suffix_declined');

    if (externalPage) {
      // URL externa configurada explicitamente (accepted_page / recused_page)
      const eid = `external-${++extCtr}`;
      mainNodes.push({
        id: eid,
        type: 'externalRedirectNode',
        data: {
          type: 'externalRedirect',
          label: i18n.t('funnel:external_redirect'),
          url: externalPage,
          source: `${upsell.name} (${suffix})`,
        } satisfies ExternalRedirectNodeData,
        position: { x: 0, y: 0 },
      });
      mainEdges.push(
        mkEdge(`e-${unid}-ext-${eid}`, unid, eid, {
          color: edgeColor,
          srcH,
          dash: true,
          desc: i18n.t(direction === 'accept' ? 'funnel:edge_accept_external' : 'funnel:edge_decline_external'),
        }),
      );
    } else if (data.product.thankyou_url) {
      // Sem URL configurada no upsell → redireciona para o thankyou_url do produto
      const tyid = `thankyou-${unid}-${direction}`;
      if (!mainNodes.find((n) => n.id === tyid)) {
        mainNodes.push({
          id: tyid,
          type: 'thankYouNode',
          data: {
            type: 'thankYou',
            label: i18n.t('funnel:thank_you_page', 'Página de Obrigado'),
            url: data.product.thankyou_url,
            source: `${upsell.name} (${suffix})`,
          } satisfies ThankYouNodeData,
          position: { x: 0, y: 0 },
        });
      }
      mainEdges.push(
        mkEdge(`e-${unid}-ty-${direction}`, unid, tyid, {
          color: EC.thankYou,
          srcH,
          desc: i18n.t(direction === 'accept' ? 'funnel:edge_accept_thankyou' : 'funnel:edge_decline_thankyou'),
        }),
      );
    } else {
      // Sem URL — nó de conclusão de compra
      const cid = `completion-${++cmpCtr}`;
      mainNodes.push({
        id: cid,
        type: 'completionNode',
        data: {
          type: 'completion',
          label: i18n.t('funnel:purchase_completion'),
          source: `${upsell.name} (${suffix})`,
        } satisfies CompletionNodeData,
        position: { x: 0, y: 0 },
      });
      mainEdges.push(
        mkEdge(`e-${unid}-${direction === 'accept' ? 'acc' : 'dec'}-${cid}`, unid, cid, {
          color: edgeColor,
          srcH,
          desc: i18n.t(direction === 'accept' ? 'funnel:edge_accept_completion' : 'funnel:edge_decline_completion'),
        }),
      );
    }
  }

  const visitedChain = new Set<string>();

  function addChain(uid: string, srcId: string, srcH?: string) {
    const u = upsellMap.get(uid);
    if (!u) return;

    const unid = `upsell-${u.id}`;

    // Cria a edge mesmo que o node já exista (caso de ciclo)
    const edgeColor =
      srcH === 'decline' ? EC.decline : srcH === 'accept' ? EC.accept : EC.purchase;

    const targetName = u.name;
    const edgeDesc =
      srcH === 'purchase'
        ? i18n.t('funnel:edge_purchase_approved', { name: targetName })
        : srcH === 'decline'
          ? i18n.t('funnel:edge_decline_target', { name: targetName })
          : srcH === 'accept'
            ? i18n.t('funnel:edge_accept_target', { name: targetName })
            : undefined;

    mainEdges.push(
      mkEdge(`e-${srcId}-up-${u.id}`, srcId, unid, {
        color: edgeColor,
        srcH,
        desc: edgeDesc,
      }),
    );

    // Ciclo detectado — não recursar
    if (visitedChain.has(uid)) return;
    visitedChain.add(uid);

    const uPerf = data.performance?.upsells?.[u.id];
    if (!mainNodes.find((n) => n.id === unid)) {
      mainNodes.push({
        id: unid,
        type: 'upsellNode',
        data: {
          type: 'upsell',
          label: u.name,
          funnelType: u.funnel_type,
          page: u.page,
          offerName: u.offer_name ?? '',
          priceFormatted: u.offer_price_formatted ?? '',
          upsellId: u.id,
          productId: data.product.id,
          imageUrl: u.image_url ?? undefined,
          hasAcceptTarget: !!u.accept_target_id,
          hasDeclineTarget: !!u.decline_target_id,
          hasExternalAccept: !u.accept_target_id && !!u.accepted_page,
          hasExternalDecline: !u.decline_target_id && !!u.recused_page,
          upSellDisplayText: u.up_sell_display_text,
          upSellButtonColor: u.up_sell_button_color,
          upSellDisplayTextColor: u.up_sell_display_text_color,
          downSellDisplayText: u.down_sell_display_text,
          downSellButtonColor: u.down_sell_button_color,
          downSellDisplayTextColor: u.down_sell_display_text_color,
          conversions: uPerf?.conversions,
          revenue: uPerf?.revenue,
        } satisfies UpsellNodeData,
        position: { x: 0, y: 0 },
      });
    }

    if (u.accept_target_id) {
      addChain(u.accept_target_id, unid, 'accept');
    } else {
      addTerminalNode(u, unid, 'accept');
    }

    if (u.decline_target_id) {
      addChain(u.decline_target_id, unid, 'decline');
    } else {
      addTerminalNode(u, unid, 'decline');
    }
  }

  if (data.product_to_first_upsell) {
    addChain(data.product_to_first_upsell, prodId, 'purchase');
  } else if (data.product.thankyou_url) {
    // Sem upsell → direto para a página de obrigado
    const tyid = `thankyou-product`;
    mainNodes.push({
      id: tyid,
      type: 'thankYouNode',
      data: {
        type: 'thankYou',
        label: i18n.t('funnel:thank_you_page', 'Página de Obrigado'),
        url: data.product.thankyou_url,
        source: data.product.name,
      } satisfies ThankYouNodeData,
      position: { x: 0, y: 0 },
    });
    mainEdges.push(
      mkEdge(`e-prod-ty`, prodId, tyid, {
        color: EC.thankYou,
        srcH: 'purchase',
        desc: i18n.t('funnel:edge_purchase_thankyou', 'Após compra'),
      }),
    );
  } else {
    const cid = `completion-${++cmpCtr}`;
    mainNodes.push({
      id: cid,
      type: 'completionNode',
      data: { type: 'completion', label: i18n.t('funnel:purchase_completion'), source: data.product.name } satisfies CompletionNodeData,
      position: { x: 0, y: 0 },
    });
    mainEdges.push(
      mkEdge(`e-prod-cmp-${cid}`, prodId, cid, {
        color: EC.purchase,
        srcH: 'purchase',
        desc: i18n.t('funnel:edge_purchase_completion'),
      }),
    );
  }

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'TB',
    nodesep: 280,
    ranksep: 250,
    marginx: 120,
    marginy: 100,
  });

  mainNodes.forEach((n) => {
    const w = NODE_W[n.type || 'productFunnelNode'] || 280;
    const h = NODE_H[n.type || 'productFunnelNode'] || 100;
    g.setNode(n.id, { width: w, height: h });
  });
  mainEdges.forEach((e) => g.setEdge(e.source, e.target));
  Dagre.layout(g);

  mainNodes.forEach((n) => {
    const pos = g.node(n.id);
    const w = NODE_W[n.type || 'productFunnelNode'] || 280;
    const h = NODE_H[n.type || 'productFunnelNode'] || 100;
    n.position = { x: pos.x - w / 2, y: pos.y - h / 2 };
  });

  // Linhas paralelas: quando two edges têm o mesmo source→target (ex: accept e decline
  // apontam para o mesmo upsell), deslocar lateralmente para exibir as duas visíveis.
  const edgePairMap = new Map<string, Edge[]>();
  mainEdges.forEach((e) => {
    const key = `${e.source}→${e.target}`;
    if (!edgePairMap.has(key)) edgePairMap.set(key, []);
    edgePairMap.get(key)!.push(e);
  });
  edgePairMap.forEach((edgesForPair) => {
    if (edgesForPair.length === 2) {
      // Ordenar: accept (verde) vai para esquerda (-), decline (vermelho) vai para direita (+)
      const sorted = [...edgesForPair].sort((a, b) => {
        const aIsAccept = (a.data as any)?.color === EC.accept;
        return aIsAccept ? -1 : 1;
      });
      (sorted[0].data as any).parallelOffset = -14;
      (sorted[1].data as any).parallelOffset = 14;
    }
  });

  // Back-edges (ciclos): redirecionar para handles laterais com path customizado
  const nodeById = new Map(mainNodes.map((n) => [n.id, n]));

  mainEdges.forEach((e) => {
    const srcNode = nodeById.get(e.source);
    const tgtNode = nodeById.get(e.target);
    if (!srcNode || !tgtNode) return;

    const srcW = NODE_W[srcNode.type || ''] || 280;
    const srcH = NODE_H[srcNode.type || ''] || 100;
    const tgtW = NODE_W[tgtNode.type || ''] || 280;
    const tgtH = NODE_H[tgtNode.type || ''] || 100;

    const srcCenterY = srcNode.position.y + srcH / 2;
    const tgtCenterY = tgtNode.position.y + tgtH / 2;

    if (tgtCenterY < srcCenterY - 10) {
      const srcCenterX = srcNode.position.x + srcW / 2;
      const tgtCenterX = tgtNode.position.x + tgtW / 2;

      // Mesmo lado para source e target — evita cruzar conteúdo dos nodes
      const side: 'left' | 'right' = tgtCenterX < srcCenterX - 100 ? 'left' : 'right';

      e.sourceHandle = side === 'left' ? 'source-left' : 'source-right';
      e.targetHandle = side === 'left' ? 'target-left' : 'target-right';

      e.style = {
        ...e.style,
        strokeDasharray: '8 4',
      };
      e.data = {
        ...(e.data as Record<string, unknown>),
        isBackEdge: true,
        backEdgeSide: side,
      };
    }
  });

  const AUX_H_GAP = 120;
  const AUX_V_PAD = 20;

  auxNodes.forEach((a) => {
    const parent = mainNodes.find((n) => n.id === a.parentId);
    if (!parent) return;
    const pw = NODE_W[parent.type || 'productFunnelNode'] || 280;
    const aw = NODE_W[a.node.type || 'orderBumpNode'] || 260;
    const ah = NODE_H[a.node.type || 'orderBumpNode'] || 155;

    const xPos =
      a.side === 'right'
        ? parent.position.x + pw + AUX_H_GAP
        : parent.position.x - aw - AUX_H_GAP;

    const parentCenterY = parent.position.y + (NODE_H[parent.type || 'productFunnelNode'] || 100) / 2;
    const totalGroupH = a.total * ah + (a.total - 1) * AUX_V_PAD;
    const groupStartY = parentCenterY - totalGroupH / 2;
    const yPos = groupStartY + a.idx * (ah + AUX_V_PAD);

    a.node.position = { x: xPos, y: yPos };
  });

  const allNodes = [...mainNodes, ...auxNodes.map((a) => a.node)];
  const allEdges = [...mainEdges, ...auxEdges];

  return { nodes: allNodes, edges: allEdges };
}
